
export const deepMerge = (target, source) => {
    const isObject = (obj) => obj && typeof obj === 'object' && !Array.isArray(obj);
    const output = { ...target };

    if (isObject(target) && isObject(source)) {
        Object.keys(source).forEach(key => {
            if (isObject(source[key])) {
                if (key in target && isObject(target[key])) {
                    output[key] = deepMerge(target[key], source[key]);
                } else {
                    output[key] = source[key];
                }
            } else {
                output[key] = source[key];
            }
        });
    }
    return output;
};

export const generateTranslationSchema = (listService, factoryService, pageCollection, currentTranslations = {}) => {
    const schema = {};
    const languages = listService.getAllLanguages();

    const addPropsToSchema = (id, model, factoryKey, pageModel) => {
        const props = model.get("translateProps");
        if (!props || props.length === 0) return;

        const originalId = listService.getCleanedId(id) || id;

        // Ensure a base object for the ID exists
        if (!schema[originalId]) {
            schema[originalId] = {};
        }

        // Add metadata
        schema[originalId] = {
            ...schema[originalId], // Preserve existing data from potential deep merge
            Name: model.get("name"),
            Type: factoryKey,
        };

        if (pageModel) {
            schema[originalId].Page = pageModel.get("name");
            schema[originalId].PageId = pageModel.get("id");
        }

        // Add language placeholders for each translatable property
        props.forEach(p => {
            if (!schema[originalId][p.key]) {
                schema[originalId][p.key] = {};
            }

            // --- Get and add the default value ---
            const defaultValue = model.get(p.key) || "";
            schema[originalId][p.key]['default-readonly'] = defaultValue;
            
            languages.forEach(locale => {
                const existingValue = currentTranslations?.[originalId]?.[p.key]?.[locale.code];
                schema[originalId][p.key][locale.code] = typeof existingValue === 'string' ? existingValue : "";
        
            });
        });
    };

    // Process App-level properties
    const app = listService.getObject("aForm").model;
    addPropsToSchema("app", app, "app");

    // Process Pages and their controls
    pageCollection.models.forEach(pageModel => {
        const pageId = pageModel.id;
        addPropsToSchema(pageId, pageModel, "page");

        const controls = listService.getPageControls(pageId);
        controls.forEach(control => {
            const model = control.model;
            const controlId = model.get("id");
            const factoryKey = model.get('factoryKey');
            const factoryName = factoryService.factories[factoryKey].name;
            addPropsToSchema(controlId, model, factoryName, pageModel);

            if (factoryKey === 'control_table') {
                const originalTableId = listService.getCleanedId(controlId) || controlId;
                if (!schema[originalTableId].columns) {
                    schema[originalTableId].columns = {};
                }

                const gridData = model.get('gridData');
                if (gridData && Array.isArray(gridData)) {
                    gridData.forEach(column => {
                        const columnId = column.id || column.get("id");
                        const originalColumnId = listService.getCleanedId(columnId) || columnId;
                        const columnProps = column.translateProps || column.get("translateProps");

                        if (!columnId || !columnProps) return;

                        schema[originalTableId].columns[originalColumnId] = {
                            Name: column.name || column.get("name"),
                            Type: column.factoryKey || column.get("factoryKey"),
                        };

                        columnProps.forEach(prop => {
                            schema[originalTableId].columns[originalColumnId][prop.key] = {};

                            // --- Get and add the default value for columns ---
                            const defaultValue = column.get(prop.key) || "";
                            schema[originalTableId].columns[originalColumnId][prop.key]['default-readonly'] = defaultValue;

                            languages.forEach(locale => {
                                const existingValue = currentTranslations?.[originalTableId]?.columns?.[originalColumnId]?.[prop.key]?.[locale.code];
                                schema[originalTableId].columns[originalColumnId][prop.key][locale.code] = typeof existingValue === 'string' ? existingValue : "";
                            });
                        });
                    });
                }
            }
        });
    });

    return schema;
};

export const generateControlsTreeData = (listService, pageCollection, showEmptyDefaults) => {
    const allPagesTreeData = [];

    // Helper to add translatable properties as children to a node
    const addTranslationPropsToNode = (model, node, options = {}) => {
        const translateProps = model.get('translateProps');
        if (!translateProps) return;

        translateProps.forEach((prop) => {

            const defaultValue = model.get(prop.key);

            // If the user wants to hide empty defaults, and the value is empty or just whitespace, skip this property.
            if (!showEmptyDefaults && (!defaultValue || !defaultValue.trim())) {
                return; // Skips to the next iteration of the forEach loop
            }

            node.children.push({
                id: `${node.id}-${prop.key}`,
                key: prop.key,
                obj: options.isColumn ? options.tableId : node.id, // If column, obj is the parent table's ID
                columnId: options.isColumn ? node.id : undefined, // Add columnId if it's a column property
                name: prop.label,
                isProperty: true,
                parent: node.parent,
            });
        });
    };
    
    // Helper to recursively build node data for a control and its children
    const buildControlNode = (controlObject, pageId) => {
        const objectModel = controlObject.model || controlObject;
        const factoryKey = objectModel.get('factoryKey');
        const transProps = objectModel.get('translateProps');
       
        // Only create a node if the object itself has translatable properties
        const hasTranslatableContent = (transProps && transProps.some(p => showEmptyDefaults || (objectModel.get(p.key) && objectModel.get(p.key).trim()))) || 
                                     (factoryKey === 'control_table' && (objectModel.get('gridData') || []).length > 0);

        if (!hasTranslatableContent) {
            return null;
        }

        const node = {
            id: objectModel.get("id"),
            name: objectModel.get('name'),
            type: factoryKey,
            parent: pageId,
            children: []
        };
        
        addTranslationPropsToNode(objectModel, node);

        if (factoryKey === 'control_table') {
            const gridData = objectModel.get('gridData');
            if (gridData && Array.isArray(gridData)) {
                gridData.forEach(columnModel => {
                    const columnProps = columnModel.get('translateProps');
                    if(columnProps && columnProps.length > 0) {
                        const columnNode = {
                            // Node data for a COLUMN
                            id: columnModel.get("id"),
                            name: columnModel.get("name"),
                            type: columnModel.get("factoryKey"),
                            parent: node.id, // Parent is the table
                            children: []
                        };

                        addTranslationPropsToNode(columnModel, columnNode, { isColumn: true, tableId: node.id });

                        if (columnNode.children.length > 0) {
                            node.children.push(columnNode);
                        }
                    }
                });
            }
        }
        
        const childrenToIterate = objectModel.get('childList') || [];
        childrenToIterate.forEach((childObj) => {
            const childNode = buildControlNode(childObj, node.id);
            if (childNode) {
                node.children.push(childNode);
            }
        });

        return node.children.length > 0 ? node : null;
    };


    // 1. Create the root "App" node
    const appNode = {
        id: 'app',
        name: self.mainKind === 'R' ? 'Report' : 'App',
        isPage: true,
        children: [ {
            id: 'app-title',
            key: 'title',
            name: 'App Title',
            obj: 'app',
            isProperty: true,
            parent: 'app'
        }]
    };
    allPagesTreeData.push(appNode);

    // 2. Create nodes for each page and its controls
    pageCollection.models.forEach(pageModel => {
        const pageId = pageModel.id;
        const pageNode = {
            id: pageId,
            name: pageModel.get("name") || `Page ${pageId}`,
            isPage: true,
            parent: 'app',
            children: []
        };

        addTranslationPropsToNode(pageModel, pageNode);

        const controlsArray = listService.getPageControls(pageId);
        controlsArray.forEach((controlObj) => {
            if (!controlObj.groupId) {
                const controlTreeNode = buildControlNode(controlObj, pageId);
                if (controlTreeNode) {
                    pageNode.children.push(controlTreeNode);
                }
            }
        });

        if (pageNode.children.length > 0) {
            allPagesTreeData.push(pageNode);
        }
    });

    return allPagesTreeData;
};
